import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { XMarkIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import { weeklyProgramsAPI, workoutPlannerAPI } from '../../services/api'
import { getWeekNumber } from './workoutHelpers'
import { SessionQuantityPicker } from './SessionQuantityPicker'

export function ProgramFormModal({ program, onClose, onSuccess }) {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()
  const currentWeek = getWeekNumber(new Date())

  const [formData, setFormData] = useState({
    week_number: program?.week_number || currentWeek,
    year: program?.year || currentYear,
    weekly_notes: program?.weekly_notes || ''
  })
  // VH-111: quantity map { [template_id]: count } gathered from the picker
  // before the form is submitted. Translated to a repeated template_ids[]
  // on submit (backend's create endpoint enumerates the list as-is).
  const [quantityMap, setQuantityMap] = useState({})

  const { data: templatesData } = useQuery({
    queryKey: ['exerciseModules'],
    queryFn: () => workoutPlannerAPI.getTemplates({})
  })

  const templates = templatesData?.data?.templates || []

  const mutation = useMutation({
    mutationFn: program
      ? (data) => weeklyProgramsAPI.updateProgram(program.id, data)
      : weeklyProgramsAPI.createProgram,
    onSuccess: onSuccess
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    // Expand quantityMap { tid: count } -> [tid, tid, ...] N times.
    const templateIds = []
    for (const [tid, count] of Object.entries(quantityMap)) {
      const n = Number(count) || 0
      const id = Number(tid)
      for (let i = 0; i < n; i++) templateIds.push(id)
    }
    const submitData = {
      ...formData,
      template_ids: templateIds.length > 0 ? templateIds : undefined
    }
    mutation.mutate(submitData)
  }

  const handleChange = (e) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }))
  }


  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {program ? t('programs.editProgram') : t('programs.createProgram')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('programs.form.weekNumber')} *
              </label>
              <input
                type="number"
                name="week_number"
                required
                min="1"
                max="53"
                value={formData.week_number}
                onChange={handleChange}
                className="w-full focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('programs.year')} *
              </label>
              <input
                type="number"
                name="year"
                required
                min="2020"
                max="2030"
                value={formData.year}
                onChange={handleChange}
                className="w-full focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('programs.form.weeklyNotes')}
            </label>
            <textarea
              name="weekly_notes"
              value={formData.weekly_notes}
              onChange={handleChange}
              rows="2"
              placeholder={t('programs.form.weeklyNotesPlaceholder')}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          {!program && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('programs.selectModule')}
              </label>
              <SessionQuantityPicker
                templates={templates}
                hideSubmitButton
                onQuantityChange={setQuantityMap}
              />
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? t('programs.form.saving') : program ? t('programs.form.update') : t('programs.form.create')}
            </button>
          </div>

          {mutation.isError && (
            <div className="text-sm text-red-600 mt-2">
              {t('common.error')}: {mutation.error?.response?.data?.detail || mutation.error?.message || t('programs.form.errorSaving')}
              {mutation.error?.response?.status && (
                <span className="text-gray-500 ml-2">(Status: {mutation.error.response.status})</span>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export function CopyProgramModal({ sourceProgram, onClose, onSuccess }) {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()
  const currentWeek = getWeekNumber(new Date())

  const [targetYear, setTargetYear] = useState(currentYear)
  const [targetWeek, setTargetWeek] = useState(currentWeek + 1)

  const mutation = useMutation({
    mutationFn: (data) => weeklyProgramsAPI.copyProgram(sourceProgram.id, data),
    onSuccess: () => {
      alert(t('programs.copySuccess'))
      onSuccess()
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    mutation.mutate({
      target_year: targetYear,
      target_week: targetWeek
    })
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('programs.copyTo')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            {t('programs.week')} {sourceProgram.week_number}, {sourceProgram.year}
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('programs.form.selectWeek')} *
              </label>
              <input
                type="number"
                required
                min="1"
                max="53"
                value={targetWeek}
                onChange={(e) => setTargetWeek(Number(e.target.value))}
                className="w-full focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('programs.form.selectYear')} *
              </label>
              <input
                type="number"
                required
                min="2020"
                max="2030"
                value={targetYear}
                onChange={(e) => setTargetYear(Number(e.target.value))}
                className="w-full focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center"
              disabled={mutation.isPending}
            >
              <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
              {mutation.isPending ? t('common.loading') : t('programs.copy')}
            </button>
          </div>

          {mutation.isError && (
            <div className="text-sm text-red-600 mt-2">
              {t('common.error')}: {mutation.error?.response?.data?.detail || mutation.error?.message || t('programs.form.errorSaving')}
              {mutation.error?.response?.status && (
                <span className="text-gray-500 ml-2">(Status: {mutation.error.response.status})</span>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}
